import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FolderKanban, Plus, Zap, FileText } from "lucide-react";
import { PROJECT_STATUSES, statusMeta } from "@/lib/taskman";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hideDoCharge, setHideDoCharge] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", name: "", description: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (clientFilter !== "all") params.client_id = clientFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const [p, c] = await Promise.all([
        api.get("/projects", { params }), api.get("/clients"),
      ]);
      setProjects(p.data.projects || []);
      setClients(Array.isArray(c.data) ? c.data : []);
    } catch { toast.error("Failed to load projects"); }
    finally { setLoading(false); }
  }, [clientFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const createProject = async () => {
    if (!form.client_id || !form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/projects", {
        ...form, name: form.name.trim(),
        start_date: form.start_date || null, end_date: form.end_date || null,
      });
      toast.success("Project created");
      setCreateOpen(false);
      setForm({ client_id: "", name: "", description: "", start_date: "", end_date: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 space-y-4 max-w-[1400px]" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold tracking-tight flex items-center gap-2">
            <FolderKanban className="h-6 w-6 text-[#002FA7]" /> Projects
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Proposals become projects on acceptance. Every client also has a default "Do &amp; Charge" project for ad-hoc work.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-project-btn">
          <Plus className="h-4 w-4 mr-1" /> New project
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-56" data-testid="project-filter-client">
            <SelectValue placeholder="All clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="project-filter-status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {PROJECT_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer select-none ml-1"
          data-testid="project-filter-hide-do-charge-wrap">
          <input type="checkbox" className="h-3.5 w-3.5 rounded-sm border-zinc-300 text-[#002FA7] focus:ring-[#002FA7]/40"
            checked={hideDoCharge}
            onChange={(e) => setHideDoCharge(e.target.checked)}
            data-testid="project-filter-hide-do-charge" />
          Hide Do &amp; Charge projects
        </label>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {projects.filter((p) => !hideDoCharge || !p.is_default).map((p) => {
            const st = statusMeta(PROJECT_STATUSES, p.status);
            const tc = p.task_counts || { total: 0, done: 0 };
            const pct = tc.total ? Math.round((tc.done / tc.total) * 100) : 0;
            return (
              <Link key={p.project_id} to={`/projects/${p.project_id}`}
                data-testid={`project-card-${p.project_id}`}>
                <Card className="hover:shadow-md transition-shadow h-full">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm text-zinc-900 leading-snug">{p.name}</div>
                      <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${st.cls}`}>{st.label}</span>
                    </div>
                    <div className="text-xs text-zinc-500">{p.client_name}</div>
                    <div className="flex items-center gap-2 text-[10px]">
                      {p.is_default && (
                        <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm font-semibold uppercase">
                          <Zap className="h-3 w-3" /> Do &amp; Charge
                        </span>
                      )}
                      {p.source_proposal_id && (
                        <span className="inline-flex items-center gap-1 text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-sm font-semibold uppercase">
                          <FileText className="h-3 w-3" /> From proposal
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                        <span>{tc.done}/{tc.total} tasks done</span><span>{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="new-project-dialog">
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
            <DialogDescription className="text-xs">Create a project to group and schedule client work.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Project name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="project-name-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Start date</Label>
                <Input type="date" value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">End date</Label>
                <Input type="date" value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createProject} disabled={saving || !form.client_id || !form.name.trim()}
              data-testid="project-create-btn">
              {saving ? "Creating…" : "Create project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
